// Re-export shared Twilio utilities for convenience
export { isTwilioDefaultUrl } from '@/shared/twilio';

/**
 * Parse Twilio API URL to extract edge location and region code
 * @param apiUrl - The Twilio API URL (e.g., "https://api.frankfurt.ie1.twilio.com")
 * @returns Object with edge and region, or null
 */
export const parseTwilioRegion = (apiUrl?: string): { edge: string; region: string } | null => {
  if (!apiUrl) {
    return null;
  }
  try {
    const hostname = new URL(apiUrl).hostname;
    // Legacy URL defaults to ashburn/us1
    if (hostname === 'api.twilio.com') {
      return { edge: 'ashburn', region: 'us1' };
    }
    // Format: api.{edge}.{region}.twilio.com
    const parts = hostname.split('.');
    if (parts.length >= 4 && hostname.endsWith('.twilio.com')) {
      // Normalize edge name: replace hyphens with underscores for translation keys
      const edge = parts[1].replace(/-/g, '_');
      const region = parts[2];
      return { edge, region };
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Checks if a voice_url points to a Moveo channels webhook.
 * Moveo URLs follow the pattern: https://channels.{*}.moveo.ai/v1/twiliovoice/{uuid}
 * Not used in current classification logic — kept for future use to distinguish
 * Moveo-owned numbers from third-party webhooks.
 */
export const isMoveoChannelsUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }
  return /channels\.[^/]*moveo\.ai\/v1\/twiliovoice\//.test(url);
};

/**
 * Get a short badge label for a Twilio voice region.
 * Groups European regions as "EU" for simplicity.
 */
export const getRegionBadgeLabel = (region: string | undefined): string | null => {
  if (!region) {
    return null;
  }
  const regionMap: Record<string, string> = {
    us1: 'US',
    ie1: 'EU',
    de1: 'EU',
    au1: 'AU',
    br1: 'BR',
    jp1: 'JP',
    sg1: 'SG',
  };
  return regionMap[region.toLowerCase()] ?? region.toUpperCase();
};
